// src/ui/screens/History.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db, listRecentSessions, type Session, type ResultSummary } from "../../storage/db";
import { colors } from "../design-system/colors";
import { fontFamily, typeScale } from "../design-system/typography";
import { space, radius, minTapTarget } from "../design-system/spacing";

interface Row {
  session: Session;
  summary: ResultSummary | undefined;
}

const RISK_SYMBOL: Record<string, string> = { safe: "○", caution: "△", danger: "×" };
const RISK_COLOR: Record<string, string> = {
  safe: colors.safe,
  caution: colors.caution,
  danger: colors.danger,
};

export default function History() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    void (async () => {
      const sessions = await listRecentSessions(50);
      const withSummaries = await Promise.all(
        sessions.map(async (session) => ({
          session,
          summary: await db.resultSummaries.get(session.id),
        })),
      );
      setRows(withSummaries);
    })();
  }, []);

  return (
    <div style={styles.container}>
      <button onClick={() => navigate("/measure")} style={styles.backButton}>
        ← 戻る
      </button>
      <h1 style={styles.title}>履歴</h1>

      {rows.length === 0 && (
        <p style={styles.empty}>まだ計測記録がありません。計測を開始してみましょう。</p>
      )}

      <div style={styles.list}>
        {rows.map(({ session, summary }) => (
          <button
            key={session.id}
            style={styles.row}
            onClick={() => navigate(`/result/${session.id}`)}
          >
            <div
              style={{
                ...styles.riskDot,
                color: RISK_COLOR[summary?.riskLevel ?? "safe"],
              }}
            >
              {RISK_SYMBOL[summary?.riskLevel ?? "safe"]}
            </div>
            <div style={{ flex: 1, textAlign: "left" }}>
              <div style={styles.rowTitle}>
                {session.exerciseType === "DEADLIFT" ? "デッドリフト" : "スクワット"}
              </div>
              <div style={styles.rowDate}>
                {new Date(session.startedAt).toLocaleString("ja-JP", {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </div>
            <div style={styles.rowScore}>{Math.round(summary?.overallScore ?? 0)}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100dvh",
    background: colors.bg,
    color: colors.textPrimary,
    fontFamily: fontFamily.ui,
    padding: space.lg,
    maxWidth: 480,
    margin: "0 auto",
  },
  backButton: {
    background: "none",
    border: "none",
    color: colors.textSecondary,
    fontFamily: fontFamily.ui,
    fontSize: typeScale.body.size,
    padding: 0,
    marginBottom: space.md,
    minHeight: minTapTarget,
    cursor: "pointer",
  },
  title: { fontSize: typeScale.h1.size, marginBottom: space.lg },
  empty: { color: colors.textSecondary, fontSize: typeScale.body.size },
  list: { display: "flex", flexDirection: "column", gap: space.sm },
  row: {
    display: "flex",
    alignItems: "center",
    gap: space.md,
    minHeight: 64,
    padding: space.md,
    borderRadius: radius.md,
    background: colors.surface,
    border: `1px solid ${colors.surfaceBorder}`,
    cursor: "pointer",
  },
  riskDot: { fontSize: "1.25rem", fontWeight: 700, width: 24, textAlign: "center" },
  rowTitle: { fontFamily: fontFamily.ui, fontWeight: 600, color: colors.textPrimary },
  rowDate: { fontSize: typeScale.caption.size, color: colors.textSecondary },
  rowScore: {
    fontFamily: fontFamily.numeric,
    fontSize: typeScale.h2.size,
    fontWeight: 700,
    color: colors.textPrimary,
  },
};
